import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@exhousing/design-system";
import { useState } from "react";
import { toast } from "sonner";

import { Check, Pencil, X } from "lucide-react";

import {
  useAddDeduction,
  useCloseDeposit,
  useDeposit,
  useUpdateDeduction,
  useUpsertDeposit,
} from "@/hooks/api/useDeposit";
import type { components } from "@/types/api";

type Deposit = components["schemas"]["DepositResponse"];
type Deduction = components["schemas"]["DeductionResponse"];

// TODO: replace with generated field after openapi regen adds refund_due_date
// to DepositResponse.
type DepositWithRefundDate = Deposit & { refund_due_date?: string | null };

function DepositReceiptForm({
  contractId,
  deposit,
}: {
  contractId: string;
  deposit: Deposit;
}) {
  const upsert = useUpsertDeposit(contractId);
  const [receivedDate, setReceivedDate] = useState(deposit.received_date ?? "");
  const [invoiceRef, setInvoiceRef] = useState(deposit.invoice_ref ?? "");

  return (
    <form
      className="flex flex-wrap items-end gap-2 border-t border-border/60 pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        upsert.mutate(
          {
            amount: deposit.amount,
            received_date: receivedDate || null,
            invoice_ref: invoiceRef || null,
          },
          {
            onSuccess: () => toast.success("Deposit receipt saved"),
            onError: () => toast.error("Could not save receipt"),
          },
        );
      }}
    >
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">Received date</p>
        <Input
          type="date"
          value={receivedDate}
          onChange={(e) => setReceivedDate(e.target.value)}
          className="h-10 w-40"
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">Invoice ref</p>
        <Input
          value={invoiceRef}
          onChange={(e) => setInvoiceRef(e.target.value)}
          className="h-10 w-40"
        />
      </div>
      <Button type="submit" size="sm" variant="outline" disabled={upsert.isPending}>
        Save receipt
      </Button>
    </form>
  );
}

function euro(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
}

const DEDUCTION_TYPES = [
  "damage",
  "cleaning",
  "repair",
  "replacement",
  "unpaid_rent",
  "admin",
  "other",
];

/** A deduction line that the agent can re-price inline. Check-out deductions can
 *  arrive at €0 (no amount captured yet), so editing the amount here is the
 *  primary way to settle them before closing the deposit. */
function DeductionRow({
  contractId,
  deduction,
  editable,
}: {
  contractId: string;
  deduction: Deduction;
  editable: boolean;
}) {
  const update = useUpdateDeduction(contractId);
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(deduction.amount));

  const save = () => {
    const next = Number(amount);
    if (Number.isNaN(next) || next < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    update.mutate(
      { deductionId: deduction.id, body: { amount: next } },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success("Deduction updated");
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <li className="flex items-center justify-between gap-2 border-b border-border/60 pb-1.5 last:border-0">
      <span className="capitalize">
        {deduction.deduction_type.replace(/_/g, " ")}
        {deduction.description ? ` — ${deduction.description}` : ""}
      </span>
      {editing ? (
        <span className="flex items-center gap-1.5">
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-8 w-24 text-right tabular-nums"
            autoFocus
          />
          <button
            type="button"
            onClick={save}
            disabled={update.isPending}
            className="text-success hover:opacity-70 disabled:opacity-40"
            aria-label="Save amount"
          >
            <Check className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setAmount(String(deduction.amount));
              setEditing(false);
            }}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Cancel"
          >
            <X className="size-4" />
          </button>
        </span>
      ) : (
        <span className="flex items-center gap-2">
          <span className="tabular-nums text-muted-foreground">
            {euro(deduction.amount)}
          </span>
          {editable && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Edit amount"
            >
              <Pencil className="size-3.5" />
            </button>
          )}
        </span>
      )}
    </li>
  );
}

export function DepositCard({ contractId }: { contractId: string }) {
  const { data: deposit } = useDeposit(contractId);
  const upsert = useUpsertDeposit(contractId);
  const addDeduction = useAddDeduction(contractId);
  const close = useCloseDeposit(contractId);

  const [amount, setAmount] = useState("");
  const [dedType, setDedType] = useState("damage");
  const [dedAmount, setDedAmount] = useState("");
  const [dedDesc, setDedDesc] = useState("");

  const closed = deposit?.status === "closed";
  // TODO: read deposit.refund_due_date directly once openapi regen adds it.
  const refundDueDate =
    (deposit as DepositWithRefundDate | null | undefined)?.refund_due_date ?? null;

  if (!deposit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Deposit</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!amount) return;
              upsert.mutate(
                { amount: Number(amount) },
                { onError: () => toast.error("Could not create deposit") },
              );
            }}
          >
            <Input
              type="number"
              placeholder="Deposit amount (€)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-48"
            />
            <Button type="submit" size="sm" disabled={upsert.isPending}>
              Create deposit
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Deposit</CardTitle>
        <Badge tone={closed ? "neutral" : "success"} dot>
          {deposit.status}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs uppercase text-muted-foreground">Held</p>
            <p className="font-display text-xl tabular-nums">{euro(deposit.amount)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-muted-foreground">Deductions</p>
            <p className="font-display text-xl tabular-nums text-danger">
              {euro(deposit.deductions_total)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase text-muted-foreground">
              {closed ? "Refunded tenant" : "Refundable"}
            </p>
            <p className="font-display text-xl tabular-nums text-success">
              {euro(closed ? deposit.refund_to_tenant : deposit.refundable_to_tenant)}
            </p>
          </div>
        </div>

        {deposit.deductions.length > 0 && (
          <ul className="space-y-1.5 text-sm">
            {deposit.deductions.map((d) => (
              <DeductionRow
                key={d.id}
                contractId={contractId}
                deduction={d}
                editable={!closed}
              />
            ))}
          </ul>
        )}

        {/* Refund split — who gets what once the deposit is settled. */}
        {(deposit.refund_to_tenant != null || deposit.refund_to_landlord != null) && (
          <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Refund to tenant</span>
              <span className="tabular-nums font-medium text-success">
                {euro(deposit.refund_to_tenant)}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-muted-foreground">Refund to landlord</span>
              <span className="tabular-nums font-medium">
                {euro(deposit.refund_to_landlord)}
              </span>
            </div>
            {refundDueDate && (
              <div className="mt-1 flex items-center justify-between">
                <span className="text-muted-foreground">Refund due</span>
                <span className="tabular-nums">{refundDueDate}</span>
              </div>
            )}
          </div>
        )}

        {!closed && <DepositReceiptForm contractId={contractId} deposit={deposit} />}

        {!closed && (
          <>
            <form
              className="flex flex-wrap items-end gap-2 border-t border-border/60 pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!dedAmount) return;
                addDeduction.mutate(
                  {
                    deduction_type: dedType,
                    amount: Number(dedAmount),
                    description: dedDesc || null,
                  },
                  {
                    onSuccess: () => {
                      setDedAmount("");
                      setDedDesc("");
                    },
                    onError: () => toast.error("Could not add deduction"),
                  },
                );
              }}
            >
              <select
                value={dedType}
                onChange={(e) => setDedType(e.target.value)}
                className="h-10 rounded-md border border-input bg-card px-2 text-sm capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {DEDUCTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Description"
                value={dedDesc}
                onChange={(e) => setDedDesc(e.target.value)}
                className="h-10 w-40"
              />
              <Input
                type="number"
                placeholder="€"
                value={dedAmount}
                onChange={(e) => setDedAmount(e.target.value)}
                className="h-10 w-24"
              />
              <Button type="submit" size="sm" variant="outline">
                Add deduction
              </Button>
            </form>

            <Button
              variant="accent"
              size="sm"
              disabled={close.isPending}
              onClick={() =>
                close.mutate(undefined, {
                  onSuccess: () => toast.success("Deposit closed & reconciled"),
                  onError: () => toast.error("Could not close deposit"),
                })
              }
            >
              Close &amp; reconcile
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
